import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { Observable } from 'rxjs';
import { ConstsHelper } from '../../../helpers/consts.helper';
import { LoginResponseInterface } from '../../authentication/models/login-response.interface';
import { CookieService } from 'ngx-cookie-service';
import { CryptService } from './crypt.service';

@Injectable({
  providedIn: 'root'
})

export class ApiService {

  apiURL: string;
  fileApiURL: string = environment.data.fileApiUrl;
  iaApiURL: string = environment.data.iaApiUrl;

  constructor(
      private http: HttpClient,
      private cookieService: CookieService,
      private cryptService: CryptService
  ) {
    const splitHostname = window.location.hostname.split('.');
    if (splitHostname.length > 0 && splitHostname[0].includes('extec')) {
      this.apiURL = environment.data.extEcApiUrl;
    } else {
      this.apiURL = environment.data.apiUrl;
    }
  }

  /**
   * Get generic method.
   * @param route (endpoint)
   */
  public get<T>(route: string) {
    return this.http.get<T>(`${this.apiURL}${route}`, this.headers());
  }

  /**
   * Get generic method.
   * @param route (endpoint)
   */
  public getForIA<T>(route: string) {
    return this.http.get<T>(`${this.iaApiURL}${route}`, this.headers());
  }

  /**
   * Post generic method.
   * @param route (endpoint)
   * @param object (json)
   */
  public post<T>(route: string, object: object): Observable<T> {
    object = Object.assign(
        {group: 'ec'},
        object
    );
    return this.http.post<T>(`${this.apiURL}${route}`, object, this.headers());
  }

  /**
   * Get files list
   * @param route (endpoint)
   * @param object (json)
   */
  public getFiles(route: string, object: object): Observable<any> {
    return this.http.post<any>(`${this.fileApiURL}${route}`, object, { headers: this.headers().headers });
  }

  /**
   * Post generic method.
   * @param route (endpoint)
   * @param object (json)
   */
  public getFile<Blob>(route: string, object: object): Observable<Blob> {
    return this.http.post<Blob>(`${this.fileApiURL}${route}`, object, { headers: this.headers().headers, responseType: 'blob' as 'json' });
  }

  /**
   * @param route (endpoint)
   * @param object (json)
   */
  public getFileUrl(route: string, object: object): Observable<any> {
    return this.http.post<any>(`${this.fileApiURL}${route}`, object, { headers: this.headers().headers });
  }

  /**
   * @param route (endpoint)
   */
  public downloadFile<Blob>(route: string): Observable<Blob> {
    return this.http.get<Blob>(`${this.fileApiURL}${route}`, { headers: this.headers().headers, responseType: 'blob' as 'json' });
  }

  /**
   * Post file method.
   * @param route
   * @param formData
   */
  public postFile<T>(route: string, formData: FormData): Observable<T> {
    const options = {
      headers: {
        "Accept": "application/json",
        Authorization: 'Bearer ' + this.getToken().token
      }
    };

    return this.http.post<any>(`${this.fileApiURL}${route}`, formData, options);
  }

  /**
   * Put generic method.
   * @param route (endpoint)
   * @param object (json)
   */
  public put<T>(route: string, object: object): Observable<T> {
    object = Object.assign(
        {group: 'ec'},
        object
    );
    return this.http.put<T>(`${this.apiURL}${route}`, object, this.headers());
  }

  /**
   * Delete generic method.
   * @param route (endpoint)
   * @param object (json)
   */
  public delete<T>(route: string): Observable<T> {
    return this.http.delete<T>(`${this.apiURL}${route}`, this.headers());
  }

  /**
   * Builds and returns the headers according to the state of the user
   * (authenticated = we add his token to headers)
   */
  private headers() {
    const options = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    if (this.getToken() != null) {
      options.headers['Authorization'] = 'Bearer ' + this.getToken().token;
    }
    return options;
  }

  /**
   * Builds and returns the headers according to the state of the user
   * (authenticated = we add his token to headers)
   */
  private getFileHeader() {
    const options = {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/pdf'
      }
    };

    if (this.getToken() != null) {
      options.headers['Authorization'] = 'Bearer ' + this.getToken().token;
    }

    return options;
  }

  /**
   * Get token information
   */
  public getToken(): LoginResponseInterface {
    if (this.cookieService.get(ConstsHelper.COOKIE_CUSTOMER_TOKEN)) {
      return JSON.parse(this.cryptService.decrypt(this.cookieService.get(ConstsHelper.COOKIE_CUSTOMER_TOKEN)));
    }
    return null;
  }
}
